import Sizes from '$store/components/product/Sizes.tsx'
import ShelfTimer from '$store/components/timer/ShelfTimer.tsx'
import AddToCartButton from '$store/islands/AddToCartButton.tsx'
import { SealConfig } from '$store/loaders/seals.tsx'
import { TimerConfig } from '$store/loaders/timers.tsx'
import { sendEvent } from '$store/sdk/analytics.ts'
import { formatPrice } from '$store/sdk/format.ts'
import { bestInstallment, useOffer } from '$store/sdk/useOffer.ts'
import { useVariantPossibilities } from '$store/sdk/useVariantPossibilities.ts'
import { getCheapestVariantOffers } from '$store/utils/getCheapestVariantOffers.ts'
import { useSignal } from '@preact/signals'
import type { Product } from 'apps/commerce/types.ts'
import { mapProductToAnalyticsItem } from '$store/utils/productToAnalyticsItem.ts'
import Image from 'apps/website/components/Image.tsx'
import Icon from 'site/components/ui/Icon.tsx'
import WishlistButton from 'site/components/wishlist/WishlistButton.tsx'
import c from 'site/sdk/c.ts'
import { IsVariantOf, SlimProduct } from 'site/types.ts'
import calcOFF from 'site/sdk/calcOFF.ts'
import { ButtonColorsProps } from 'site/components/product/ProductShelf.tsx'
import { ToAddToWishlist } from 'site/sdk/ga4/events/add_to_wishlist.ts'
import { GenericAnalyticsEvent } from 'site/components/Analytics.tsx'
import { ModalBuyTrigger } from '$store/islands/ModalBuy.tsx'
import { SIZE_ORDER } from 'site/utils/sizeOrder.ts'
import { ModalSimilarsTrigger } from 'site/components/product/ModalSimilars.tsx'
import BuyTogetherAnalysis from 'site/components/product-analysis/BuyTogether.tsx'
import type { AlgoliaHitInsight } from 'site/utils/algoliaInsights.ts'

// A shelf/card só usa a dimensão de tamanho das variantes (`possibilities.TAMANHO`
// / `possibilities.Tamanho` em ProductCard, ModalBuy, ShelfItem e KitLook). Todas as
// outras propriedades VTEX (cor + dezenas de specs) eram serializadas no state de
// hidratação sem nunca serem lidas — o maior ofensor de egress da PLP. Mantemos só o
// tamanho para reduzir drasticamente o payload sem alterar comportamento.
const SIZE_PROPERTY_NAMES = new Set(['TAMANHO', 'Tamanho'])

export const mapProductToSlimProduct = ({
    url,
    productID,
    name,
    image,
    offers,
    brand,
    category,
    isVariantOf,
    inProductGroupWithID,
}: Product): SlimProduct => {
    return {
        url,
        productID,
        name,
        image,
        offers,
        brand,
        category,
        inProductGroupWithID,
        isVariantOf: isVariantOf
            ? ({
                name: isVariantOf.name,
                productGroupID: isVariantOf.productGroupID,
                // Nível de grupo não é lido no path do card (só no ProductInfo da PDP,
                // que usa o Product completo). Zeramos para não serializar à toa.
                additionalProperty: [],
                hasVariant: isVariantOf.hasVariant.map(
                    ({ additionalProperty, offers, url, sku }) => ({
                        additionalProperty: (additionalProperty ?? []).filter(
                            (property) => SIZE_PROPERTY_NAMES.has(property.name ?? ''),
                        ),
                        sku,
                        offers: offers
                            ? {
                                lowPrice: offers.lowPrice,
                                offers: offers.offers.map(
                                    ({
                                        inventoryLevel,
                                        priceSpecification,
                                        seller,
                                        price,
                                    }) => ({
                                        inventoryLevel,
                                        seller,
                                        price,
                                        // sends only the best price and the list price specifications
                                        priceSpecification: [
                                            priceSpecification.find(
                                                (spec) =>
                                                    spec.priceType ===
                                                        'https://schema.org/ListPrice',
                                            ),
                                            priceSpecification.reduce(bestInstallment, null),
                                        ],
                                    }),
                                ),
                            }
                            : undefined,
                        url,
                    }),
                ),
                // TODO: improve this type and ship it
            } as IsVariantOf)
            : undefined,
    }
}

export interface Props {
    product: SlimProduct
    renderModalBuy?: boolean
    isMobile?: boolean
    /** Preload card image */
    preload?: boolean
    /** @title Porcentagem de desconto no pix */
    pixDiscount?: number
    hidePixDiscountInShelf?: boolean
    /**
     * @description Usado para evento do google analytics
     */
    itemListId?: string
    /**
     * @description usado para evento do google analytics
     */
    itemListName?: string
    /**
     * @title Selo
     */
    seals: SealConfig[]
    /**
     * @title Cronômetro
     */
    timers: TimerConfig[]

    /** @title Cores do fundo do botão */
    backgroundColors?: ButtonColorsProps
    wishlist_analytics?: ToAddToWishlist
    add_to_cart_showcase?: GenericAnalyticsEvent
    modalSimilarsRenderHorizontally?: boolean
    renderModalSimilars?: boolean
    algoliaHit?: AlgoliaHitInsight
}

function ProductCard({
    product,
    renderModalBuy,
    isMobile,
    pixDiscount,
    hidePixDiscountInShelf,
    preload,
    itemListName,
    itemListId,
    seals = [],
    timers,
    backgroundColors,
    wishlist_analytics,
    add_to_cart_showcase,
    modalSimilarsRenderHorizontally,
    renderModalSimilars,
    algoliaHit,
}: Props) {
    const { url, productID, name, image: images, offers, isVariantOf, inProductGroupWithID } =
        product

    const [front, back] = images ?? []
    const selectedSkuId = useSignal(null as string | null)

    const { productGroupID } = isVariantOf ?? {}

    // If variant is not selected, display cheapest variant
    const offersToDisplay = selectedSkuId.value && isVariantOf
        ? isVariantOf.hasVariant.find(({ sku }) => sku === selectedSkuId.value)?.offers
        : isVariantOf
        ? getCheapestVariantOffers(isVariantOf.hasVariant)
        : offers

    const {
        listPrice,
        price,
        seller,
        bestInstallment: installment,
    } = useOffer(offersToDisplay)

    const hasDiscount = !!price && price > 0 && !!listPrice && listPrice > price

    const isAvailable = isVariantOf?.hasVariant.some((variant) => {
        return variant.offers?.offers.some((offer) => (offer.inventoryLevel.value ?? 0) > 0)
    })

    const hasTriedToBuy = useSignal(false)

    const possibilities = useVariantPossibilities(product as unknown as Product)

    const options = Object.entries(possibilities.TAMANHO ?? possibilities.Tamanho ?? {})
        .sort(([a], [b]) => a.localeCompare(b.padStart(2, '0')))
        .sort(([a], [b]) => SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b))

    const clickEvent = {
        name: 'select_item' as const,
        params: {
            item_list_name: itemListName,
            item_list_id: itemListId,
            items: [
                mapProductToAnalyticsItem({
                    product: product as unknown as Product,
                    price,
                    listPrice,
                    item_list_name: itemListName,
                    item_list_id: itemListId,
                }),
            ],
        },
    }
    const availableOptions = options.filter(([, info]) => info.isAvailable)

    selectedSkuId.value = availableOptions.length === 1
        ? availableOptions[0][1].urls[0].split('=').pop() ?? null
        : selectedSkuId.value

    const hasVariants = availableOptions.length > 1

    const parsedUrl = url?.split('?').shift()

    const topLeftSeals = seals
        .filter((seal) => seal.position === 'top-left')

    const bottomLeftSeals = seals
        .filter((seal) => !seal.position || seal.position === 'bottom-left')

    const bottomRightSeals = seals
        .filter((seal) => seal.position === 'bottom-right')

    const isHidePixDiscount = hidePixDiscountInShelf || pixDiscount === 0

    return (
        // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
        <div
            data-deco='view-product'
            id={`product-card-${productID}`}
            class='w-full h-full group p-0 rounded-b-[6px]
      opacity-100 max-lg:group-data-[not-visible]/slide:opacity-50 transition-opacity'
            onClick={() => {
                sendEvent(clickEvent)
            }}
        >
            <a
                href={parsedUrl}
                class={c(
                    'group-data-[cols-layout="cols-1"]/gallery:flex-row h-full flex flex-col overflow-hidden',
                    modalSimilarsRenderHorizontally && '!flex-row',
                )}
                {...(algoliaHit
                    ? {
                        'data-insights-object-id': algoliaHit.objectId,
                        'data-insights-position': String(algoliaHit.position),
                        'data-insights-query-id': algoliaHit.queryId,
                    }
                    : {})}
            >
                <div
                    class={c(
                        'group-data-[cols-layout="cols-1"]/gallery:max-w-[147px] group-data-[cols-layout="cols-1"]/gallery:mr-5 relative w-full overflow-hidden',
                        modalSimilarsRenderHorizontally && 'max-w-[119px] mr-3',
                    )}
                >
                    {/* Bottom-left seals */}
                    <div class='absolute z-10 bottom-[10px] left-[10px] flex flex-col gap-2'>
                        <div class='flex flex-col gap-1'>
                            {bottomLeftSeals.map((seal) => {
                                const s = seal?.size?.split('x')

                                return (
                                    <div
                                        class={c(
                                            'flex items-center text-xs px-2 font-bold leading-none',
                                            !seal.image && 'py-1.5 px-2.5 max-w-fit',
                                        )}
                                        style={{
                                            backgroundColor: seal?.bgColor ?? '#ffffff',
                                            color: seal?.color ?? '#171843',
                                            // backgroundImage: seal?.image ? `url(${seal?.image})` : undefined,
                                            backgroundRepeat: 'no-repeat',
                                            backgroundSize: 'contain',
                                            width: seal.image && s?.[0] && `${s[0]}px`,
                                            height: seal.image && s?.[1] && `${s[1]}px`,
                                        }}
                                    >
                                        {seal?.image
                                            ? <img src={seal.image} alt='' />
                                            : seal?.label}
                                    </div>
                                )
                            })}
                        </div>

                        {/* Discount */}
                        {hasDiscount && (
                            <div class='w-fit px-2 py-0.5 bg-secondary-700 text-white font-bold text-xs lg:text-base'>
                                {calcOFF(listPrice, price).toFixed(0)}% OFF
                            </div>
                        )}
                    </div>

                    {/* Top-left seals */}
                    <div class='absolute z-10 top-[10px] left-[10px] flex flex-col gap-1 max-w-[calc(100%-70px)]'>
                        {topLeftSeals.map((seal) => {
                            const s = seal?.size?.split('x')

                            return (
                                <div
                                    class={c(
                                        'flex items-center text-xs px-2 font-bold leading-none',
                                        !seal.image && 'py-1.5 px-2.5 max-w-fit',
                                    )}
                                    style={{
                                        backgroundColor: seal?.bgColor ?? 'transparent',
                                        color: seal?.color ?? '#171843',
                                        // backgroundImage: seal?.image ? `url(${seal?.image})` : undefined,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: 'contain',
                                        width: seal.image && s?.[0] && `${s[0]}px`,
                                        height: seal.image && s?.[1] && `${s[1]}px`,
                                    }}
                                >
                                    {seal?.image ? <img src={seal.image} alt='' /> : seal?.label}
                                </div>
                            )
                        })}
                    </div>

                    {/* Bottom-right seals */}
                    <div class='absolute z-10 bottom-[10px] right-[10px] flex flex-col gap-1 max-w-[150px]'>
                        {bottomRightSeals.map((seal) => {
                            const s = seal?.size?.split('x')

                            return (
                                <div
                                    class={c(
                                        'flex items-center text-xs px-2 font-bold leading-none',
                                        !seal.image && 'py-1.5 px-2.5 max-w-fit',
                                    )}
                                    style={{
                                        backgroundColor: seal?.bgColor ?? '#ffffff',
                                        color: seal?.color ?? '#171843',
                                        // backgroundImage: seal?.image ? `url(${seal?.image})` : undefined,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: 'contain',
                                        width: seal.image && s?.[0] && `${s[0]}px`,
                                        height: seal.image && s?.[1] && `${s[1]}px`,
                                    }}
                                >
                                    {seal?.image ? <img src={seal.image} alt='' /> : seal?.label}
                                </div>
                            )
                        })}
                    </div>

                    {/* Wishlist and ModalBuy*/}
                    <div
                        class={c(
                            'group-data-[cols-layout="cols-1"]/gallery:hidden absolute top-[10px] right-[15px] z-10 grid',
                            modalSimilarsRenderHorizontally && 'hidden',
                        )}
                    >
                        <WishlistButton
                            productGroupID={productGroupID}
                            productID={productID}
                            analytics={wishlist_analytics}
                        />
                        {product && renderModalBuy && isMobile && (
                            <ModalBuyTrigger product={{ ...product, pixDiscount }} />
                        )}
                    </div>

                    <Image
                        src={front.url ?? ''}
                        alt={front.alternateName}
                        width={298}
                        height={373}
                        class='w-full'
                        preload={preload}
                        loading={preload ? 'eager' : 'lazy'}
                    />
                    <Image
                        src={back?.url ?? front.url ?? ''}
                        alt={back?.alternateName ?? front.alternateName}
                        width={298}
                        height={373}
                        class='w-full absolute inset-0 opacity-0 md:group-hover:opacity-100 transition-opacity max-md:animate-blink'
                    />

                    <div
                        class='absolute z-40 bottom-0 hidden sm:group-hover:flex flex-col gap-4 w-full py-4 px-2 bg-white/80'
                        style={{
                            backdropFilter: 'blur(2px)',
                            display: (product.isVariantOf?.hasVariant ?? []).length < 1
                                ? 'none'
                                : undefined,
                        }}
                    >
                        <Sizes
                            hasVariants={hasVariants}
                            options={availableOptions}
                            product={product}
                            selectedSkuIdSignal={selectedSkuId}
                            hasTriedToBuy={hasTriedToBuy}
                        />
                        {selectedSkuId.value
                            ? (
                                <>
                                    <style
                                        dangerouslySetInnerHTML={{
                                            __html: `
												.addToShelf{
												background: ${backgroundColors?.backgroundColor};
												}
												.addToShelf:hover{
												background: ${backgroundColors?.hoverBackgroundColor};
												}
											`,
                                        }}
                                    />

                                    <AddToCartButton
                                        variant='addToCartShelf'
                                        item_list_id={itemListId}
                                        item_list_name={itemListName}
                                        class='addToShelf'
                                        itemsToAdd={[
                                            {
                                                skuId: selectedSkuId.value,
                                                brand: product.brand,
                                                category: product.category ?? '',
                                                productGroupID: product.isVariantOf?.productGroupID,
                                                productGroupName: product.isVariantOf?.name,
                                                listPrice: listPrice ?? price ?? 0,
                                                productID: productID,
                                                url: parsedUrl ?? '',
                                                sellerId: seller ?? '1',
                                                price: price ?? 0,
                                                discount: price && listPrice
                                                    ? listPrice - price
                                                    : 0,
                                                name: product.name ?? '',
                                                productGroupId:
                                                    product.isVariantOf?.productGroupID ??
                                                        '',
                                            },
                                        ]}
                                        add_to_cart_showcase={add_to_cart_showcase}
                                    >
                                        Comprar
                                    </AddToCartButton>
                                </>
                            )
                            : isAvailable
                            ? (
                                <strong class='font-lato text-sm text-center text-neutral-2 data-[tried-to-buy=true]:text-primary-700'>
                                    Selecione o tamanho
                                </strong>
                            )
                            : null}
                    </div>
                </div>

                <div
                    class={c(
                        'w-full h-[30px] lg:h-[34px] group-data-[cols-layout="cols-1"]/gallery:hidden group-data-[similars="true"]/similars:hidden',
                        modalSimilarsRenderHorizontally && 'hidden',
                    )}
                >
                    {product &&
                        renderModalSimilars && (
                        <ModalSimilarsTrigger product={{ ...product, pixDiscount }} />
                    )}
                </div>

                <div class='flex flex-col md:gap-0 flex-1 relative'>
                    <h3
                        class={c(
                            'font-bold font-lato h-[50px] leading-none line-clamp-3 mt-4 text-ellipsis text-neutral-0 text-left group-data-[similars="true"]/similars:text-sm group-data-[similars="true"]/similars:!leading-none',
                            modalSimilarsRenderHorizontally && '!mt-0',
                        )}
                    >
                        {isVariantOf?.name ?? name}
                    </h3>
                    <div class='yv-review-quickreview !hidden' value={productGroupID} />

                    {isAvailable
                        ? (
                            <div class='flex flex-col gap-1'>
                                <div class='flex items-center gap-x-2 flex-row'>
                                    {listPrice && price && listPrice > price && (
                                        <div class='text-light text-sm text-neutral-2 line-through'>
                                            {formatPrice(listPrice)}
                                        </div>
                                    )}
                                    <div class='font-bold font-lato text-lg text-primary-700 group-data-[similars="true"]/similars:text-base'>
                                        {price && formatPrice(price)}
                                    </div>
                                </div>

                                {installment?.billingIncrement &&
                                    installment.billingDuration &&
                                    installment.billingDuration > 1 && (
                                    <div class='text-neutral-1 flex items-center gap-2 text-sm'>
                                        <Icon id='Card' width={21} height={15} />
                                        <div>
                                            <span class='font-semibold'>Parcele</span>{' '}
                                            <strong>{installment.billingDuration}x</strong> de{' '}
                                            <strong>
                                                {formatPrice(installment.billingIncrement)}
                                            </strong>
                                        </div>
                                    </div>
                                )}

                                {!isHidePixDiscount && (
                                    <div class='text-neutral-1 text-sm'>
                                        <span>ou</span> <strong>{pixDiscount}%</strong>{' '}
                                        <strong>de desconto no Pix</strong>
                                    </div>
                                )}

                                {/* COMPRE JUNTO NA PÁGINA PRODUCT VIEW ANALYSIS */}

                                <BuyTogetherAnalysis productID={inProductGroupWithID ?? ''} />

                                <div class='hidden group-data-[cols-layout="cols-1"]/gallery:grid grid-cols-[auto_auto] justify-start items-center gap-x-3 group-data-[similars="true"]/similars:hidden'>
                                    {product && renderModalBuy && isMobile && (
                                        <ModalBuyTrigger product={product} />
                                    )}
                                    <WishlistButton
                                        productGroupID={productGroupID}
                                        productID={productID}
                                        analytics={wishlist_analytics}
                                    />
                                </div>
                            </div>
                        )
                        : (
                            <div class='flex flex-col gap-2 font-lato text-neutral-1'>
                                <span class='text-lg font-extrabold'>Produto Indisponível</span>
                                <a
                                    href={url}
                                    target='_self'
                                    class='font-semibold text-sm hover:underline'
                                >
                                    Avise-me quando estiver disponível!
                                </a>
                            </div>
                        )}

                    <ShelfTimer timers={timers} />

                    {
                        /* <SendEventOnLoad
						event={{
							name: "view_item_list",
							params: {
								item_list_name: name,
								items: [
									mapProductToAnalyticsItem({
											product,
											...useOffer(product.offers),
									}),
							],
							},
						}}
					/>  */
                    }
                </div>
            </a>
        </div>
    )
}

export default ProductCard
